"use strict";
/**
 * Framework Configuration
 *
 * Central configuration for all compliance frameworks.
 * Defines columns, plan availability, and template mappings.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FRAMEWORK_PLAN_LIMITS = exports.FRAMEWORK_CONFIGS = exports.SubscriptionPlan = exports.ComplianceFramework = void 0;
exports.getFrameworkConfig = getFrameworkConfig;
exports.getAvailableFrameworks = getAvailableFrameworks;
exports.isFrameworkAvailable = isFrameworkAvailable;
exports.detectFrameworkFromHeaders = detectFrameworkFromHeaders;
exports.getFrameworkColumnKeys = getFrameworkColumnKeys;
exports.normalizeStatus = normalizeStatus;
exports.getFrameworksByCategory = getFrameworksByCategory;
exports.getFrameworksByRegion = getFrameworksByRegion;
// Import existing types to avoid duplication
const types_1 = require("./types");
Object.defineProperty(exports, "ComplianceFramework", { enumerable: true, get: function () { return types_1.ComplianceFramework; } });
const subscription_1 = require("./types/subscription");
Object.defineProperty(exports, "SubscriptionPlan", { enumerable: true, get: function () { return subscription_1.SubscriptionPlan; } });
// ============================================================================
// FRAMEWORK CONFIGURATIONS
// ============================================================================
exports.FRAMEWORK_CONFIGS = {
    GDPR: {
        id: 'GDPR',
        name: 'General Data Protection Regulation',
        shortName: 'GDPR',
        description: 'EU data protection and privacy regulation',
        region: 'EU',
        category: 'privacy',
        templateFile: 'gdpr_processing_activities.csv',
        headerIdentifiers: ['processing_activity', 'legal_basis', 'data_categories'],
        statusColumn: 'status',
        availableOn: ['FREE', 'PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'processing_activity', label: 'Processing Activity', type: 'text', sortable: true },
            { key: 'legal_basis', label: 'Legal Basis', type: 'badge', sortable: true },
            { key: 'data_categories', label: 'Data Categories', type: 'text' },
            { key: 'data_subjects', label: 'Data Subjects', type: 'text' },
            { key: 'recipients', label: 'Recipients', type: 'text' },
            { key: 'retention_period', label: 'Retention', type: 'text' },
            { key: 'third_country_transfers', label: 'Transfers', type: 'badge' },
            { key: 'transfer_mechanism', label: 'Mechanism', type: 'text' },
            { key: 'dpia_required', label: 'DPIA Req.', type: 'badge' },
            { key: 'dpia_completed', label: 'DPIA Done', type: 'badge' },
            { key: 'technical_measures', label: 'Technical Measures', type: 'text' },
            { key: 'organizational_measures', label: 'Org. Measures', type: 'text' },
            { key: 'responsible_person', label: 'Responsible', type: 'text', sortable: true },
            { key: 'status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
        ],
    },
    NIS2: {
        id: 'NIS2',
        name: 'Network and Information Security Directive 2',
        shortName: 'NIS2',
        description: 'EU cybersecurity directive for essential services',
        region: 'EU',
        category: 'security',
        templateFile: 'nis2_assessment.csv',
        headerIdentifiers: ['control_id', 'control_name', 'category', 'requirement'],
        statusColumn: 'status',
        availableOn: ['FREE', 'PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'control_id', label: 'Control ID', type: 'text', sortable: true, width: '100px' },
            { key: 'control_name', label: 'Control Name', type: 'text', sortable: true },
            { key: 'category', label: 'Category', type: 'badge', sortable: true },
            { key: 'requirement', label: 'Requirement', type: 'text' },
            { key: 'status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
            { key: 'evidence', label: 'Evidence', type: 'text' },
            { key: 'notes', label: 'Notes', type: 'text' },
            { key: 'responsible', label: 'Responsible', type: 'text', sortable: true },
            { key: 'due_date', label: 'Due Date', type: 'date', sortable: true },
        ],
    },
    DORA: {
        id: 'DORA',
        name: 'Digital Operational Resilience Act',
        shortName: 'DORA',
        description: 'EU regulation for ICT risk management in financial services',
        region: 'EU',
        category: 'financial',
        templateFile: 'dora_ict_register.csv',
        headerIdentifiers: ['ict_asset_name', 'asset_type', 'recovery_time_objective'],
        statusColumn: 'status',
        availableOn: ['PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'ict_asset_name', label: 'ICT Asset Name', type: 'text', sortable: true },
            { key: 'asset_type', label: 'Asset Type', type: 'badge', sortable: true },
            { key: 'criticality', label: 'Criticality', type: 'badge', sortable: true },
            { key: 'vendor', label: 'Vendor', type: 'text', sortable: true },
            { key: 'contract_end_date', label: 'Contract End', type: 'date', sortable: true },
            { key: 'risk_assessment_done', label: 'Risk Assessed', type: 'badge' },
            { key: 'risk_level', label: 'Risk Level', type: 'badge', statusColors: { 'HIGH': 'red', 'MEDIUM': 'yellow', 'LOW': 'green', 'CRITICAL': 'red' } },
            { key: 'controls_in_place', label: 'Controls', type: 'text' },
            { key: 'incident_response_plan', label: 'IR Plan', type: 'badge' },
            { key: 'recovery_time_objective', label: 'RTO', type: 'text' },
            { key: 'recovery_point_objective', label: 'RPO', type: 'text' },
            { key: 'last_resilience_test', label: 'Last Test', type: 'date' },
            { key: 'concentration_risk', label: 'Conc. Risk', type: 'badge' },
            { key: 'exit_strategy', label: 'Exit Strategy', type: 'badge' },
            { key: 'status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
        ],
    },
    EPRIVACY: {
        id: 'EPRIVACY',
        name: 'ePrivacy Directive',
        shortName: 'ePrivacy',
        description: 'EU directive on privacy in electronic communications',
        region: 'EU',
        category: 'privacy',
        templateFile: 'eprivacy_cookies.csv',
        headerIdentifiers: ['cookie_name', 'cookie_type', 'consent_required'],
        statusColumn: 'status',
        availableOn: ['FREE', 'PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'cookie_name', label: 'Cookie Name', type: 'text', sortable: true },
            { key: 'cookie_type', label: 'Type', type: 'badge', sortable: true },
            { key: 'provider', label: 'Provider', type: 'text', sortable: true },
            { key: 'purpose', label: 'Purpose', type: 'text' },
            { key: 'duration', label: 'Duration', type: 'text' },
            { key: 'data_collected', label: 'Data Collected', type: 'text' },
            { key: 'consent_required', label: 'Consent Req.', type: 'badge' },
            { key: 'consent_obtained', label: 'Consent OK', type: 'badge' },
            { key: 'privacy_policy_link', label: 'Privacy Policy', type: 'link' },
            { key: 'status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
        ],
    },
    AI_ACT: {
        id: 'AI_ACT',
        name: 'EU AI Act',
        shortName: 'AI Act',
        description: 'EU regulation on artificial intelligence systems',
        region: 'EU',
        category: 'ai',
        templateFile: 'ai_act_systems.csv',
        headerIdentifiers: ['system_name', 'system_type', 'risk_category'],
        statusColumn: 'status',
        availableOn: ['PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'system_name', label: 'System Name', type: 'text', sortable: true },
            { key: 'system_type', label: 'System Type', type: 'badge', sortable: true },
            { key: 'risk_category', label: 'Risk Category', type: 'badge', sortable: true, statusColors: { 'High Risk': 'red', 'Limited Risk': 'yellow', 'Minimal Risk': 'green' } },
            { key: 'intended_purpose', label: 'Purpose', type: 'text' },
            { key: 'deployment_status', label: 'Deployment', type: 'badge' },
            { key: 'provider', label: 'Provider', type: 'text', sortable: true },
            { key: 'eu_database_registered', label: 'EU DB Reg.', type: 'badge' },
            { key: 'technical_documentation', label: 'Tech Docs', type: 'badge' },
            { key: 'human_oversight', label: 'Human Oversight', type: 'badge' },
            { key: 'transparency_measures', label: 'Transparency', type: 'text' },
            { key: 'data_governance', label: 'Data Gov.', type: 'text' },
            { key: 'accuracy_measures', label: 'Accuracy', type: 'text' },
            { key: 'robustness_measures', label: 'Robustness', type: 'text' },
            { key: 'conformity_assessment', label: 'Conformity', type: 'badge' },
            { key: 'status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
        ],
    },
    ISO27001: {
        id: 'ISO27001',
        name: 'ISO/IEC 27001',
        shortName: 'ISO 27001',
        description: 'International standard for information security management',
        region: 'GLOBAL',
        category: 'security',
        templateFile: 'iso27001_controls.csv',
        headerIdentifiers: ['control_ref', 'control_name', 'control_category'],
        statusColumn: 'implementation_status',
        availableOn: ['PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'control_ref', label: 'Control Ref', type: 'text', sortable: true, width: '100px' },
            { key: 'control_name', label: 'Control Name', type: 'text', sortable: true },
            { key: 'control_category', label: 'Category', type: 'badge', sortable: true },
            { key: 'implementation_status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
            { key: 'evidence_reference', label: 'Evidence', type: 'text' },
            { key: 'last_review_date', label: 'Last Review', type: 'date' },
            { key: 'next_review_date', label: 'Next Review', type: 'date', sortable: true },
            { key: 'risk_if_not_implemented', label: 'Risk', type: 'badge', statusColors: { 'HIGH': 'red', 'MEDIUM': 'yellow', 'LOW': 'green' } },
            { key: 'notes', label: 'Notes', type: 'text' },
        ],
    },
    SOC2: {
        id: 'SOC2',
        name: 'SOC 2 Type II',
        shortName: 'SOC 2',
        description: 'Service organization control report for trust services criteria',
        region: 'US',
        category: 'security',
        templateFile: 'soc2_controls.csv',
        headerIdentifiers: ['criteria_ref', 'criteria_name', 'control_activity'],
        statusColumn: 'implementation_status',
        availableOn: ['PROFESSIONAL', 'ENTERPRISE'],
        columns: [
            { key: 'criteria_ref', label: 'Criteria Ref', type: 'text', sortable: true, width: '100px' },
            { key: 'criteria_name', label: 'Criteria Name', type: 'text', sortable: true },
            { key: 'category', label: 'Category', type: 'badge', sortable: true },
            { key: 'control_activity', label: 'Control Activity', type: 'text' },
            { key: 'implementation_status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
            { key: 'evidence_reference', label: 'Evidence', type: 'text' },
            { key: 'control_owner', label: 'Owner', type: 'text', sortable: true },
            { key: 'test_frequency', label: 'Test Freq.', type: 'badge' },
            { key: 'last_tested', label: 'Last Tested', type: 'date' },
            { key: 'test_result', label: 'Test Result', type: 'badge', statusColors: { 'PASS': 'green', 'FAIL': 'red', 'N/A': 'gray' } },
            { key: 'gaps_identified', label: 'Gaps', type: 'text' },
        ],
    },
    HIPAA: {
        id: 'HIPAA',
        name: 'Health Insurance Portability and Accountability Act',
        shortName: 'HIPAA',
        description: 'US healthcare data protection regulation',
        region: 'US',
        category: 'healthcare',
        templateFile: 'hipaa_safeguards.csv',
        headerIdentifiers: ['safeguard_ref', 'safeguard_name', 'safeguard_type'],
        statusColumn: 'implementation_status',
        availableOn: ['ENTERPRISE'],
        columns: [
            { key: 'safeguard_ref', label: 'Reference', type: 'text', sortable: true, width: '120px' },
            { key: 'safeguard_name', label: 'Safeguard Name', type: 'text', sortable: true },
            { key: 'safeguard_type', label: 'Type', type: 'badge', sortable: true },
            { key: 'requirement', label: 'Requirement', type: 'text' },
            { key: 'implementation_status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
            { key: 'evidence_reference', label: 'Evidence', type: 'text' },
            { key: 'responsible_person', label: 'Responsible', type: 'text', sortable: true },
            { key: 'review_date', label: 'Review Date', type: 'date' },
            { key: 'notes', label: 'Notes', type: 'text' },
        ],
    },
    PCI_DSS: {
        id: 'PCI_DSS',
        name: 'Payment Card Industry Data Security Standard',
        shortName: 'PCI DSS',
        description: 'Security standard for handling payment card data',
        region: 'GLOBAL',
        category: 'financial',
        templateFile: 'pci_dss_saq.csv',
        headerIdentifiers: ['requirement_ref', 'requirement_name', 'sub_requirement'],
        statusColumn: 'implementation_status',
        availableOn: ['ENTERPRISE'],
        columns: [
            { key: 'requirement_ref', label: 'Req. #', type: 'text', sortable: true, width: '80px' },
            { key: 'requirement_name', label: 'Requirement', type: 'text', sortable: true },
            { key: 'sub_requirement', label: 'Sub-Requirement', type: 'text' },
            { key: 'implementation_status', label: 'Status', type: 'status', sortable: true, statusColors: { 'YES': 'green', 'PARTIAL': 'yellow', 'NO': 'red' } },
            { key: 'evidence_reference', label: 'Evidence', type: 'text' },
            { key: 'compensating_control', label: 'Comp. Control', type: 'text' },
            { key: 'last_assessment_date', label: 'Last Assessed', type: 'date' },
            { key: 'assessor_notes', label: 'Assessor Notes', type: 'text' },
        ],
    },
};
exports.FRAMEWORK_PLAN_LIMITS = {
    FREE: {
        frameworks: ['GDPR', 'NIS2', 'EPRIVACY'],
        maxOrganizations: 1,
        maxProjectsPerOrg: 2,
        maxRecordsPerProject: 50,
        maxScansPerMonth: 5,
        exportFormats: ['csv'],
        features: ['basic_reports', 'email_support'],
    },
    PROFESSIONAL: {
        frameworks: ['GDPR', 'NIS2', 'DORA', 'EPRIVACY', 'AI_ACT', 'ISO27001', 'SOC2'],
        maxOrganizations: 5,
        maxProjectsPerOrg: 10,
        maxRecordsPerProject: 500,
        maxScansPerMonth: 50,
        exportFormats: ['csv', 'pdf', 'json'],
        features: ['basic_reports', 'advanced_reports', 'priority_support', 'api_access', 'custom_templates'],
    },
    ENTERPRISE: {
        frameworks: ['GDPR', 'NIS2', 'DORA', 'EPRIVACY', 'AI_ACT', 'ISO27001', 'SOC2', 'HIPAA', 'PCI_DSS'],
        maxOrganizations: -1, // unlimited
        maxProjectsPerOrg: -1, // unlimited
        maxRecordsPerProject: -1, // unlimited
        maxScansPerMonth: -1, // unlimited
        exportFormats: ['csv', 'pdf', 'json'],
        features: ['basic_reports', 'advanced_reports', 'priority_support', 'api_access', 'custom_templates', 'sso', 'audit_log', 'dedicated_support', 'custom_integrations'],
    },
};
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
/**
 * Get framework config by ID
 */
function getFrameworkConfig(framework) {
    return exports.FRAMEWORK_CONFIGS[framework];
}
/**
 * Get all frameworks available for a plan
 */
function getAvailableFrameworks(plan) {
    const planFrameworks = exports.FRAMEWORK_PLAN_LIMITS[plan].frameworks;
    return planFrameworks.map(f => exports.FRAMEWORK_CONFIGS[f]);
}
/**
 * Check if a framework is available for a plan
 */
function isFrameworkAvailable(framework, plan) {
    return exports.FRAMEWORK_PLAN_LIMITS[plan].frameworks.includes(framework);
}
/**
 * Detect framework from CSV headers
 */
function detectFrameworkFromHeaders(headers) {
    const headerSet = new Set(headers.map(h => h.toLowerCase().trim()));
    for (const [framework, config] of Object.entries(exports.FRAMEWORK_CONFIGS)) {
        const identifiers = config.headerIdentifiers.map(h => h.toLowerCase());
        const matches = identifiers.filter(id => headerSet.has(id));
        // If all identifiers match, we found the framework
        if (matches.length === identifiers.length) {
            return framework;
        }
    }
    return null;
}
/**
 * Get column keys for a framework
 */
function getFrameworkColumnKeys(framework) {
    return exports.FRAMEWORK_CONFIGS[framework].columns.map(c => c.key);
}
/**
 * Map status string to enum value
 */
function normalizeStatus(status) {
    const normalized = status?.toUpperCase().trim() || 'PENDING';
    const validStatuses = ['YES', 'PARTIAL', 'NO', 'PENDING', 'N/A', 'PASS', 'FAIL'];
    if (normalized === 'N_A' || normalized === 'NA')
        return 'N_A';
    if (validStatuses.includes(normalized))
        return normalized;
    // Try to map common variations
    if (['COMPLETE', 'COMPLETED', 'DONE', 'TRUE', '1'].includes(normalized))
        return 'YES';
    if (['INCOMPLETE', 'IN PROGRESS', 'WIP'].includes(normalized))
        return 'PARTIAL';
    if (['FALSE', '0', 'MISSING', 'NONE'].includes(normalized))
        return 'NO';
    return 'PENDING';
}
/**
 * Get frameworks grouped by category
 */
function getFrameworksByCategory() {
    const grouped = {};
    for (const config of Object.values(exports.FRAMEWORK_CONFIGS)) {
        if (!grouped[config.category]) {
            grouped[config.category] = [];
        }
        grouped[config.category].push(config);
    }
    return grouped;
}
/**
 * Get frameworks grouped by region
 */
function getFrameworksByRegion() {
    const grouped = {};
    for (const config of Object.values(exports.FRAMEWORK_CONFIGS)) {
        if (!grouped[config.region]) {
            grouped[config.region] = [];
        }
        grouped[config.region].push(config);
    }
    return grouped;
}
